import { createAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { CoreMsgTypes, UserAccountRecord, UserRecord } from '@tassat/fred-definitions';
import { UserRecordExtended } from '@tassat/fred-extended-definitions';
import {
    Dictionary, groupBy, map, mapValues, union
} from 'lodash';

type UserRecordWithAccounts = UserRecordExtended & {
    UserAccounts: string[];
};

export interface UserState {
    [key: string]: UserRecordWithAccounts;
}

const initialState: UserState = {};

const associateUsersWithUserAccounts =
    (userAccounts: Dictionary<UserAccountRecord>, users: Dictionary<UserRecord>): Dictionary<UserRecordExtended> => {
        const groupedUserAccountsByUserId: Dictionary<UserAccountRecord[]> = groupBy(userAccounts, userAccount => userAccount.UserId);
        const mapgroupedUserAccountsByUserId: Dictionary<string[]> = mapValues(groupedUserAccountsByUserId, value => map(value, 'UserAccountId'));

        return mapValues(users, (value) => ({
            ...value,
            UserAccounts: mapgroupedUserAccountsByUserId[value.UserId] ?? []
        }));
    };

const initialLoadAction = createAction<{
    userAccounts: Dictionary<UserAccountRecord>;
    users: Dictionary<UserRecord>;
}>('InitialLoad');
const newACGMappingAction = createAction<{
    userAccounts: Dictionary<UserAccountRecord>;
    users: Dictionary<UserRecord>;
}>('NewACGMapping');
const userAccountRecordAction = createAction<UserAccountRecord>(CoreMsgTypes.UserAccountRecord);
const userRecordAction = createAction<UserRecord>(CoreMsgTypes.UserRecord);

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(userAccountRecordAction, (state, action) => {
                state[action.payload.UserId].UserAccounts =
                    union([ ...state[action.payload.UserId].UserAccounts, action.payload.UserAccountId ]);
            })
            .addCase(userRecordAction, (state, action) => {
                if (!state[action.payload.UserId]) {
                    state[action.payload.UserId] = { UserAccounts: [] as string[] } as UserRecordWithAccounts;
                }
                Object.assign(state[action.payload.UserId], action.payload);
            })
            .addMatcher(isAnyOf(initialLoadAction, newACGMappingAction), (state, action) => {
                // The purpose of newACGMappingAction case is for PCG admins
                // to receive realtime updates made to a tied account, i.e., balance update.
                const data = associateUsersWithUserAccounts(action.payload.userAccounts, action.payload.users);
                Object.assign(state, data);
            });
    }
});

export default usersSlice.reducer;